/* global BigInt */
import { USDC_DECIMALS } from '../config/contracts';

export function formatUSDC(amount) {
  return (Number(amount) / Math.pow(10, USDC_DECIMALS)).toFixed(2);
}

export function parseUSDC(amount) {
  return BigInt(Math.floor(Number(amount) * Math.pow(10, USDC_DECIMALS)));
}

export function formatAPY(apy) {
  return (Number(apy) / 10000).toFixed(4) + '%';
}

export function formatTimeRemaining(timestamp) {
  const now = Math.floor(Date.now() / 1000);
  const diff = Number(timestamp) - now;

  if (diff <= 0) return 'Ended';

  const days = Math.floor(diff / 86400);
  const hours = Math.floor((diff % 86400) / 3600);
  const minutes = Math.floor((diff % 3600) / 60);
  const seconds = diff % 60;

  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

export function getOpportunityStatus(opp, currentBlock) {
  const startBlock = Number(opp.startBlock);
  const endBlock = Number(opp.endBlock);

  if (opp.resolved) return 'resolved';
  if (opp.voided) return 'voided';

  if (currentBlock === undefined) {
    return 'monitoring';
  }

  if (currentBlock >= endBlock) return 'awaiting_resolution';
  if (currentBlock >= startBlock) return 'monitoring';
  return 'betting_open';
}

export function formatBlocksRemaining(currentBlock, targetBlock) {
  const blocksRemaining = Number(targetBlock) - Number(currentBlock);

  if (blocksRemaining <= 0) return 'Ended';

  // Estimate time remaining assuming 2 seconds per block (Base chain)
  const secondsRemaining = blocksRemaining * 2;
  const days = Math.floor(secondsRemaining / 86400);
  const hours = Math.floor((secondsRemaining % 86400) / 3600);
  const minutes = Math.floor((secondsRemaining % 3600) / 60);

  return `≈${days}d ${hours}h ${minutes}m (block ${targetBlock})`;
}

// Generate option labels and APY ranges from custom thresholds
export function generateOptionLabels(thresholds, startApyBps) {
  // Thresholds use 4-decimal precision: 10000 = 1%, so -200000 = -20%
  // Convert to percentage values (e.g., -20, -10, 0, 10, 20)
  const t = Array.isArray(thresholds)
    ? thresholds.map(th => Number(th) / 10000)
    : [
        Number(thresholds.threshold1 || thresholds[0]) / 10000,
        Number(thresholds.threshold2 || thresholds[1]) / 10000,
        Number(thresholds.threshold3 || thresholds[2]) / 10000,
        Number(thresholds.threshold4 || thresholds[3]) / 10000,
        Number(thresholds.threshold5 || thresholds[4]) / 10000,
      ];

  // startApy is also in 4-decimal precision: 10000 = 1%
  const startApyPercent = Number(startApyBps) / 10000;

  // Helper to calculate actual APY from threshold percentage change
  // E.g., if startApy = 3.38% and threshold = -25%, actual APY = 3.38 * (1 - 0.25) = 2.535%
  const calcApy = (thresholdPercent) => {
    return startApyPercent * (1 + thresholdPercent / 100);
  };

  // Format threshold as display string (without + for 0)
  const formatThreshold = (val) => {
    const formatted = val.toFixed(2);
    if (parseFloat(formatted) === 0) return '0%';
    return val > 0 ? `+${formatted}%` : `${formatted}%`;
  };

  // Format APY with 4 decimal places (contract precision)
  const formatApy = (apy) => apy.toFixed(4);

  // Helper to determine option type based on threshold range
  // 'neutral' for options that straddle 0 (negative to positive range)
  const getOptionType = (lowerBound, upperBound) => {
    if (lowerBound < 0 && upperBound > 0) return 'neutral';
    return lowerBound < 0 ? 'negative' : 'positive';
  };

  // Generate 6 options based on the 5 thresholds
  // Contract logic: bucket 0 is <= t[0], bucket 1 is > t[0] and <= t[1], etc.
  return [
    {
      main: `≤ ${formatThreshold(t[0])}`,
      sub: `≤ ${formatApy(calcApy(t[0]))}%`,
      tooltip: `APY change of ${formatThreshold(t[0])} or less (APY ≤ ${formatApy(calcApy(t[0]))}%)`,
      type: 'negative'
    },
    {
      main: `${formatThreshold(t[0])} to ${formatThreshold(t[1])}`,
      sub: `> ${formatApy(calcApy(t[0]))}% to ≤ ${formatApy(calcApy(t[1]))}%`,
      tooltip: `APY change greater than ${formatThreshold(t[0])} but at most ${formatThreshold(t[1])} (${formatApy(calcApy(t[0]))}% < APY ≤ ${formatApy(calcApy(t[1]))}%)`,
      type: getOptionType(t[0], t[1])
    },
    {
      main: `${formatThreshold(t[1])} to ${formatThreshold(t[2])}`,
      sub: `> ${formatApy(calcApy(t[1]))}% to ≤ ${formatApy(calcApy(t[2]))}%`,
      tooltip: `APY change greater than ${formatThreshold(t[1])} but at most ${formatThreshold(t[2])} (${formatApy(calcApy(t[1]))}% < APY ≤ ${formatApy(calcApy(t[2]))}%)`,
      type: getOptionType(t[1], t[2])
    },
    {
      main: `${formatThreshold(t[2])} to ${formatThreshold(t[3])}`,
      sub: `> ${formatApy(calcApy(t[2]))}% to ≤ ${formatApy(calcApy(t[3]))}%`,
      tooltip: `APY change greater than ${formatThreshold(t[2])} but at most ${formatThreshold(t[3])} (${formatApy(calcApy(t[2]))}% < APY ≤ ${formatApy(calcApy(t[3]))}%)`,
      type: getOptionType(t[2], t[3])
    },
    {
      main: `${formatThreshold(t[3])} to ${formatThreshold(t[4])}`,
      sub: `> ${formatApy(calcApy(t[3]))}% to ≤ ${formatApy(calcApy(t[4]))}%`,
      tooltip: `APY change greater than ${formatThreshold(t[3])} but at most ${formatThreshold(t[4])} (${formatApy(calcApy(t[3]))}% < APY ≤ ${formatApy(calcApy(t[4]))}%)`,
      type: getOptionType(t[3], t[4])
    },
    {
      main: `> ${formatThreshold(t[4])}`,
      sub: `> ${formatApy(calcApy(t[4]))}%`,
      tooltip: `APY change greater than ${formatThreshold(t[4])} (APY > ${formatApy(calcApy(t[4]))}%)`,
      type: 'positive'
    }
  ];
}

// Map ethers.js Result object to plain object
export function mapOpportunity(opp, id = null) {
  return {
    id,
    name: opp.name,
    startBlock: opp.startBlock,
    endBlock: opp.endBlock,
    secondsPerBlock: opp.secondsPerBlock,
    referenceTxnHash: opp.referenceTxnHash,
    startApy: opp.startApy,
    finalApy: opp.finalApy,
    resolved: opp.resolved,
    voided: opp.voided,
    winningOption: opp.winningOption,
    rewardPerToken: opp.rewardPerToken,
    durationEnum: opp.durationEnum,
    thresholds: opp.thresholds,
  };
}
